/** Date-only chronological rules for investment opportunities (timezone-safe). */

export function toDateOnly(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
}

export function todayDateOnly() {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

export function todayIso() {
  return todayDateOnly().toISOString().slice(0, 10);
}

export function addDaysIso(value, days) {
  const d = toDateOnly(value);
  if (!d) return "";
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Latest of two YYYY-MM-DD strings (or the non-empty one). */
export function maxIsoDate(...candidates) {
  const valid = candidates.filter(Boolean);
  if (valid.length === 0) return undefined;
  return valid.sort()[valid.length - 1];
}

export function isHttpImageUrl(value) {
  if (!value || typeof value !== "string") return false;
  try {
    const url = new URL(value.trim());
    if (!["http:", "https:"].includes(url.protocol)) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Returns field → message map for invalid chronological combinations.
 * Sequence: scheduled ≤ open < close ≤ start < maturity ≤ payout
 * Maturity / payout must not be before today.
 * Funding open/close and investment start may be in the past (onboarding existing deals).
 */
export function getOpportunityDateOrderErrors(values = {}) {
  const errors = {};
  const today = todayDateOnly();
  const scheduled = toDateOnly(values.scheduledPublishAt);
  const open = toDateOnly(values.fundingOpenDate);
  const close = toDateOnly(values.fundingCloseDate);
  const start = toDateOnly(values.investmentStartDate);
  const maturity = toDateOnly(values.maturityDate);
  const payout = toDateOnly(values.returnPayoutDate);

  const notPastFields = [
    [maturity, "maturityDate", "Maturity date"],
    [payout, "returnPayoutDate", "Payout date"],
  ];

  notPastFields.forEach(([date, field, label]) => {
    if (date && date < today) {
      errors[field] = `${label} cannot be in the past.`;
    }
  });

  if (open && close && !(close > open) && !errors.fundingCloseDate) {
    errors.fundingCloseDate = "Funding close must be after the funding open date.";
  }
  if (close && start && start < close && !errors.investmentStartDate) {
    errors.investmentStartDate = "Investment start cannot be before funding closes.";
  }
  if (start && maturity && !(maturity > start) && !errors.maturityDate) {
    errors.maturityDate = "Maturity date must be after the investment start date.";
  }
  if (maturity && payout && payout < maturity && !errors.returnPayoutDate) {
    errors.returnPayoutDate = "Payout date cannot be before maturity.";
  }
  if (scheduled && close && scheduled > close) {
    errors.scheduledPublishAt = "Scheduled publication must happen before funding closes.";
  } else if (scheduled && open && scheduled > open) {
    errors.scheduledPublishAt =
      "Scheduled publication must be on or before the funding open date.";
  }

  return errors;
}

export function getDateFieldMins(values = {}) {
  const today = todayIso();
  return {
    // Do not set HTML min on funding/start dates — browsers treat min as hard-disable
    // and block today/past even when onboarding existing deals. Chronology is enforced
    // by Yup + API validation messages instead.
    fundingOpenDate: undefined,
    fundingCloseDate: undefined,
    investmentStartDate: undefined,
    maturityDate: maxIsoDate(
      today,
      values.investmentStartDate ? addDaysIso(values.investmentStartDate, 1) : null
    ),
    returnPayoutDate: maxIsoDate(today, values.maturityDate || null),
    scheduledPublishAt: undefined,
    scheduledPublishAtMax: values.fundingCloseDate || undefined,
  };
}
